import { ModulationSource, type CustomContributions, type DefaultContributions, type InputRates, type Modulation, type OrgData } from 'types/dataContext';
import { CellGenContractData, CellGenContractDeltaData, CellGenCoworkerDelta, CellGenWeeklyDeltaData, CostCentreHierarchy, CoworkerGridData,
    CoworkerGridDataRef,
    CoworkerGridRow,
    CoworkerGridRowType,
    DummyGridData,
    EditableContractHoursCellProps,
    GenerateEditableCells,
    GridDataCoworkers,
    TimeSelection } from 'types/appContext';
import type { Scenario, ScenarioDelta, DummyCoworker, ContractDelta, WeeklyDelta } from 'types/scenario';
import { CoworkerAvailability, Coworker, EmploymentStatus, Coworkers } from 'types/coworker';
import { DateHelper, INDEFINITE_END_DATE, BEGINNING_OF_TIME, isoWeekFromDateString, generateScheduledWeeks, isDateOlderThanWeek,
    DATE_FORMAT_STANDARD } from 'utils/date';
import { calculateSum, roundValue } from 'utils/number';
import { costCentreIsInList, costCentresAreSame, getShortCC } from 'utils/text';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import moment from 'moment';
import type { UserProfile } from 'types/authContext';
import { isIdenticalTimeSelection, isNullOrUndefined } from 'utils/validation';
import { isCoworkersId } from 'utils/rules';
import { getHeaderContribution } from './gridFunctionsHeaders';
import { getAllCostCentresInUnit } from './gridFunctionsShared';
import { getContractCustomContributions,
    getContractDefaultContributions,
    getContributionForDelta,
    getWeeklyDeltaContributions,
    isCustomContributionActive,
    noContribution,
    selectActiveContribution } from './gridFunctionsInputFiles';

const getCostCentreDescription = (
    costCentreCode: string,
    costCentreHierarchy: CostCentreHierarchy | null | undefined
) => costCentreHierarchy?.find(({ costCentre }) => costCentre.costCentreCode === costCentreCode)?.costCentre.costCentreDescription ?? '';

/**
 * extracts the contributions in terms of hours and evenings/weekends that a coworker provides for the given cost centre.
 */
const getContractData = (
    coworker: Coworker | DummyCoworker,
    costCentre: string,
    referenceDate: string,
    timeArrayWeek: string,
    defaultContributions: DefaultContributions | undefined,
    customContributions: CustomContributions | undefined,
): CellGenContractData => {
    const isActive = coworker?.status === EmploymentStatus.ACTIVE;
    const contractHours = isActive ? coworker?.contractRange?.range.max ?? coworker?.hoursPerWeek ?? 0 : 0;
    const contractDistribution = (coworker?.costDistributions.find(cd => costCentresAreSame(cd.costCentre, costCentre))?.costCentrePercent ?? 0) / 100;
    const defContributions = getContractDefaultContributions(
        coworker,
        costCentre,
        isActive,
        defaultContributions,
    );

    const custContributions = getContractCustomContributions(
        coworker,
        costCentre,
        timeArrayWeek,
        isActive,
        customContributions,
    );
    const startDate = coworker?.contractStartDate || BEGINNING_OF_TIME;
    const endDate = coworker?.contractEndDate || INDEFINITE_END_DATE;

    return {
        startWeeksFromZero: DateHelper.getDateDifferenceInWeeks(startDate, referenceDate),
        endWeeksFromZero: DateHelper.getDateDifferenceInWeeks(endDate, referenceDate),
        weeklyHours: contractHours * contractDistribution,
        timeStamp: BEGINNING_OF_TIME,
        availability: CoworkerAvailability.HOURS,
        startDay: DateHelper.getWeekDay(startDate),
        endDay: DateHelper.getWeekDay(endDate),
        defaultContributions: defContributions,
        customContributions: custContributions,
    };
};

/**
 * Calculates the contract hours for a specific week, accounting for partial weeks when a contract starts or ends mid-week.
 * The function handles four checks:
 * 1. Contract both starts AND ends within the same week: Prorates hours based on active days within that week
 *    (e.g., Contract runs Mon-Wed (days 1-3) = weeklyHours * (2+1)/7)
 * 2. Contract starts mid-week but continues beyond it: Prorates hours from the start day to the end of the week
 *    (e.g., Starts on Thursday (day 4) = works 4 days = weeklyHours * (7 - startDay + 1)/7)
 * 3. Contract was already active, ends mid-week: Prorates hours from the beginning of the week to the end day
 *    (e.g., Ends on Wednesday (day 3) = works 3 days = weeklyHours * (3/7))
 * 4. Week is before contract starts OR after contract ends:
 *     Returns 0 hours
 * If none of the conditions above match, the coworker works the full, the full weekly hours are returned.
 * @param contractData - Contract information including weekly hours and start/end dates in weeks from zero
 * @param weeksFromZero - The week number to calculate hours for, relative to the reference date
 * @returns The prorated contract hours for the specified week, rounded to 2 decimal places
 */
const getContractHoursForWeek = (contractData: CellGenContractData, weeksFromZero: number) => {
    let hours = contractData.weeklyHours;
    if (contractData.startWeeksFromZero === weeksFromZero && contractData.endWeeksFromZero === weeksFromZero) {
        hours = contractData.weeklyHours * ((contractData.endDay - contractData.startDay + 1) / 7);
    } else if (contractData.startWeeksFromZero === weeksFromZero && contractData.endWeeksFromZero > weeksFromZero) {
        hours = contractData.weeklyHours * ((7 - contractData.startDay + 1) / 7);
    } else if (contractData.startWeeksFromZero < weeksFromZero && contractData.endWeeksFromZero === weeksFromZero) {
        hours = contractData.weeklyHours * (contractData.endDay / 7);
    } else if (contractData.startWeeksFromZero > weeksFromZero || contractData.endWeeksFromZero < weeksFromZero) {
        hours = 0;
    }

    // return Math.round(hours * 100) / 100;
    return hours;
};

const mapContractDeltasCellgen = (
    contractDeltas: ContractDelta[],
    outputArray: CellGenContractDeltaData,
    costCentre: string,
    coworker: Coworker | DummyCoworker,
    referenceDate: string,
    defaultContributions: DefaultContributions | undefined,
    customContributions: CustomContributions | undefined,
) => {
    contractDeltas.filter(contDelt => contDelt.costDistributions.some(costDist => costCentresAreSame(costDist.costCentre, costCentre)))
        .forEach(contractDelta => {
            const totalHours = contractDelta.hoursPerWeekRange ? contractDelta.hoursPerWeekRange.range.max : contractDelta.hoursPerWeek;
            const distributionInCostcentre = (contractDelta.costDistributions
                .find(cd => costCentresAreSame(cd.costCentre, costCentre))?.costCentrePercent ?? 0) / 100;
            const contributions = getContributionForDelta(
                contractDelta,
                costCentre,
                coworker,
                referenceDate,
                defaultContributions,
                customContributions,
            );
            outputArray.push({
                startWeeksFromZero: DateHelper.getDateDifferenceInWeeks(
                    isoWeekFromDateString(contractDelta.startDate),
                    referenceDate,
                ),
                endWeeksFromZero: DateHelper.getDateDifferenceInWeeks(
                    isoWeekFromDateString(contractDelta.endDate ?? INDEFINITE_END_DATE),
                    referenceDate,
                ),
                weeklyHours: totalHours * distributionInCostcentre,
                timeStamp: contractDelta.timeStamp,
                availability: CoworkerAvailability.HOURS,
                delta: contractDelta,
                defaultContributions: contributions?.defaultContributions ?? noContribution,
                customContributions: contributions?.customContributions ?? null,
            });
        });
};

const mapWeeklyDeltasCellgen = (
    weeklyDeltas: WeeklyDelta[],
    outputArray: CellGenWeeklyDeltaData,
    costCentre: string,
    referenceDate: string,
    coworker: Coworker | DummyCoworker,
    defaultContributions: DefaultContributions | undefined,
    customContributions: CustomContributions | undefined,
) => {
    weeklyDeltas
        .filter(wd => costCentresAreSame(wd.costCentre, costCentre))
        .forEach(weeklyDelta => {
            const hasCustomContribution = customContributions?.some(contribution => (
                isCoworkersId(coworker, contribution.employeeId)
                && getShortCC(contribution.contribution.costCentre) === getShortCC(costCentre)
                && isCustomContributionActive(
                    contribution.contribution.duration.startDate,
                    contribution.contribution.duration.endDate,
                    weeklyDelta.startDate,
                    weeklyDelta.endDate
                )
            )) ?? false;

            outputArray.push({
                startWeeksFromZero: DateHelper.getDateDifferenceInWeeks(
                    isoWeekFromDateString(weeklyDelta.startDate),
                    referenceDate,
                ),
                endWeeksFromZero: DateHelper.getDateDifferenceInWeeks(
                    isoWeekFromDateString(weeklyDelta.endDate),
                    referenceDate,
                ),
                weeklyHours: weeklyDelta.costCentreHours,
                timeStamp: weeklyDelta.timeStamp,
                availability: weeklyDelta.coworkerAvailability,
                delta: weeklyDelta,
                defaultContributions: getWeeklyDeltaContributions(
                    weeklyDelta,
                    costCentre,
                    defaultContributions,
                    hasCustomContribution, // If there is a custom contribution, it will be handled in the contract deltas unless weekly delta sets hours to 0
                ),
                customContributions: null, // If there is a custom contribution, it will be handled in the contract deltas
            });
        });
};

const getCoworkerModulation = (
    coworker: Coworker | DummyCoworker,
    modulationData: Modulation | undefined,
    timeArrayWeek: string,
    contractHours: number
) => {
    const dummyCoworker = { value: 0, type: CoworkerGridRowType.CONTRACT_HOURS }; // Always 0 for dummy coworkers
    const contractHoursCell = { value: contractHours, type: CoworkerGridRowType.CONTRACT_HOURS };

    if (!modulationData) {
        return coworker ? contractHoursCell : dummyCoworker;
    }
    const modulationWeek = modulationData?.result?.find(({ startDate, endDate }) => moment(timeArrayWeek).isBetween(startDate, endDate, 'isoWeek', '[]'));
    const isScheduledWeek = generateScheduledWeeks(modulationData.scheduledWeeks).includes(timeArrayWeek);

    // If the week is older than current week, we return actual hours for that week.
    if (isDateOlderThanWeek(DateHelper.getISOWeekStartDate(timeArrayWeek).format(DATE_FORMAT_STANDARD))) {
        const actualHours = modulationWeek?.actuals?.find(a => a.employeeId === coworker?.personId)?.workedHours;

        return { value: actualHours ?? 0, type: ModulationSource.ACTUALS };
    }

    if (isScheduledWeek) { // If the week is a scheduled week, we return the scheduled hours for that week, If there are no scheduled hours, we return 0.
        const scheduledHours = modulationWeek?.scheduled.find(s => s.employeeId === coworker?.personId)?.scheduleHours;

        return { value: scheduledHours ?? 0, type: ModulationSource.SCHEDULE };
    }
    const plannedHours = modulationWeek?.planned.find(p => p.employeeId === coworker?.personId);

    if (!plannedHours) {
        return coworker ? contractHoursCell : dummyCoworker;
    }
    // Calculate the planned hours for the week by adding the modulation delta and overtime hours, and subtracting the absence hours from the contract hours.
    const plannedHoursForWeek = (contractHours + plannedHours.modulationDelta + plannedHours.overtimeHours) - plannedHours.absenceHours;

    return { value: plannedHoursForWeek, type: ModulationSource.PLANNED };
};

const generateContractHoursEditableCellsProps = ({
    coworker,
    delta,
    timeArray,
    costCentre,
    defaultContributions,
    customContributions,
    modulationData,
}: GenerateEditableCells) => {
    const coworkerOrDummy = coworker ?? (delta?.dummyCoworker && Array.of(delta.dummyCoworker));
    if (timeArray.length === 0 || !coworkerOrDummy || coworkerOrDummy.length === 0) return [];

    const { personId } = coworkerOrDummy[0];
    const getContractForWeek = (week: string) => coworkerOrDummy.find(({ contractStartDate, contractEndDate }) => moment(week)
        .isBetween(contractStartDate ?? BEGINNING_OF_TIME, contractEndDate ?? INDEFINITE_END_DATE, 'isoWeek', '[]')) ?? coworkerOrDummy[0];
    const baseCoworker = getContractForWeek(timeArray[0]);

    const editableCellRow: Array<EditableContractHoursCellProps> = [];
    timeArray.forEach((week, index) => {
        /**
         * A contract switch with same cost centre during a week, need to consider all active contracts for that week to calculate the total hours.
         * eg.. Two contracts in same cost centre, one from Jan 1st to Jun 14th another from Jun 15th to Dec 31st, during the week of Jun 15th to Jun 21st,
         * Both contracts are active. We need to sum the prorated hours from both contracts for that week to get the correct total hours.
         * For contributions they are cost center level, we can take from any of the active contract per week as they will be same for cost center
          */
        const contractsForWeek = coworkerOrDummy.filter(({ contractStartDate, contractEndDate, costCentre: cc, costDistributions }) => moment(week)
            .isBetween(contractStartDate ?? BEGINNING_OF_TIME, contractEndDate ?? INDEFINITE_END_DATE, 'isoWeek', '[]')
            && (cc === costCentre || costDistributions.some(cd => costCentresAreSame(cd.costCentre, costCentre))));

        let totalHours = 0;
        let contribution = noContribution;
        contractsForWeek.forEach(contract => {
            const contractData = getContractData(contract, costCentre, timeArray[0], week, defaultContributions, customContributions);
            totalHours += getContractHoursForWeek(contractData, index);
            contribution = selectActiveContribution(index, contractData.defaultContributions, contractData.customContributions) ?? noContribution;
        });

        const { value, type } = getCoworkerModulation(coworkerOrDummy[0], (contractsForWeek.length ? modulationData : undefined), week, totalHours);
        const hours = Math.round(value * 100) / 100;

        editableCellRow.push({
            elementId: `${personId}:${costCentre}:${week}`,
            originalValue: contractsForWeek.length ? hours : 0, // Always 0 for dummy coworkers
            currentValue: hours,
            key: `${personId}:${costCentre}:${week}-cell`,
            coworkerAvailability: CoworkerAvailability.HOURS,
            contributions: contribution,
            hoursType: type
        });
    });

    if (delta) {
        const contractDeltas: CellGenContractDeltaData = [];
        const weeklyDeltas: CellGenWeeklyDeltaData = [];

        if (delta.contractDeltas?.length) {
            mapContractDeltasCellgen(delta.contractDeltas, contractDeltas, costCentre, baseCoworker, timeArray[0], defaultContributions, customContributions);
        }
        if (delta.weeklyDeltas?.length) {
            mapWeeklyDeltasCellgen(delta.weeklyDeltas, weeklyDeltas, costCentre, timeArray[0], baseCoworker, defaultContributions, customContributions);
        }
        const sortedWeeklyDeltas = weeklyDeltas.sort((a, b) => new Date(b.timeStamp).getTime() - new Date(a.timeStamp).getTime());
        const sortedContractDeltas = contractDeltas.sort((a, b) => new Date(b.timeStamp).getTime() - new Date(a.timeStamp).getTime());

        timeArray.forEach((_week, index) => {
            let coworkerDelta: CellGenCoworkerDelta;

            if (delta.coworkerDelta) {
                const coworkerContract = getContractForWeek(_week);
                const updatedCoworker = { ...coworkerContract, ...delta.coworkerDelta } as Coworker | DummyCoworker;
                coworkerDelta = {
                    ...getContractData(updatedCoworker, costCentre, timeArray[0], _week, defaultContributions, customContributions),
                    delta: delta.coworkerDelta
                };
            }
            // Note: Changes are always weekly.
            const latestWeeklyDelta = sortedWeeklyDeltas.find(
                sortedDelta => sortedDelta.startWeeksFromZero <= index && index <= sortedDelta.endWeeksFromZero,
            );
            const latestContractDelta = sortedContractDeltas.find(
                sortedDelta => sortedDelta.startWeeksFromZero <= index && index <= sortedDelta.endWeeksFromZero,
            );
            const latestCoworkerDelta = coworkerDelta && coworkerDelta.startWeeksFromZero <= index && index <= coworkerDelta.endWeeksFromZero
                ? coworkerDelta
                : undefined;

            const allDeltas = [latestContractDelta, latestWeeklyDelta, latestCoworkerDelta].filter(Boolean);

            const sortedDeltas = allDeltas.length
                ? allDeltas.sort((a, b) => new Date(b?.timeStamp ?? BEGINNING_OF_TIME).getTime() - new Date(a?.timeStamp ?? BEGINNING_OF_TIME).getTime())
                : null;
            const activeDelta = sortedDeltas?.[0];
            const activeContribution = sortedDeltas
                ?.map(del => selectActiveContribution(index, del?.defaultContributions ?? null, del?.customContributions ?? null))
                ?.find(contribution => !isNullOrUndefined(contribution)) ?? null;
            if (activeDelta) {
                editableCellRow[index].currentValue = activeDelta.weeklyHours;
                editableCellRow[index].coworkerAvailability = activeDelta.availability;
                editableCellRow[index].scenarioDeltas = {
                    latestContractDelta: latestContractDelta?.delta,
                    latestWeeklyDelta: latestWeeklyDelta?.delta,
                    latestCoworkerDelta: coworkerDelta?.delta,
                };
            }
            if (activeContribution) {
                editableCellRow[index].contributions = activeContribution;
            }
        });
    }

    return editableCellRow;
};

const getContractDelta = (personId: string, scenario: Scenario | undefined): ScenarioDelta | null => {
    if (!scenario) {
        return null;
    }

    const coworkerScenarioRaw = scenario.scenarioData.find(delta => delta.personId === personId);

    return coworkerScenarioRaw ?? null;
};

const getCoworkerCostCenters = (coworker: Coworker[] | DummyCoworker[], cwScenarioDelta: ScenarioDelta | null) => {
    const ccList = coworker?.flatMap(cw => cw.costDistributions.map(cd => cd.costCentre)) || [];

    cwScenarioDelta?.dummyCoworker?.costDistributions.forEach(cd => {
        if (!ccList.includes(cd.costCentre) && cd.costCentre) {
            ccList.push(cd.costCentre);
        }
    });

    cwScenarioDelta?.contractDeltas.forEach(delta => {
        delta?.costDistributions?.forEach(cd => {
            if (!ccList.includes(cd.costCentre) && cd.costCentre) {
                ccList.push(cd.costCentre);
            }
        });
    });

    return ccList;
};

/**
 * Function that generates the data used in the manage capacity row tables
 * for all the coworkers in the grid and any dummy coworkers in the scenario.
 * @param coworkers Array of coworkers
 * @param scenario Current scenario
 * @param timeSelection TimeSelection
 * @param costCentreList List of cost centres
 * @param hasModulationAccess If the user has access to modulation
 * @param currentUnitRates Unit rates/ Capacity factors
 * @param defaultContributions Contributions from the default contributions
 * @param customContributions Any custom contributions
 * @param modulationData Modulation data
 * @returns Array<CoworkerGridData | DummyGridData>
 */
const generateGridRows = (
    {
        coworkers,
        scenario,
        timeSelection,
        costCentreList,
        currentUnitRates,
        defaultContributions,
        customContributions,
        modulationData,
    }: {
        coworkers: Coworkers | undefined,
        scenario: Scenario | undefined,
        timeSelection: TimeSelection,
        costCentreList: Array<string>,
        currentUnitRates: InputRates | undefined,
        defaultContributions: DefaultContributions | undefined,
        customContributions: CustomContributions | undefined,
        modulationData: Modulation | undefined,
    }
) => {
    const scenarioCwsData: Array<DummyGridData> = [];
    const paDataCoworkers: Array<CoworkerGridData> = [];

    // When coworker IS dummy coworker
    scenario?.scenarioData.filter(scenDelta => scenDelta.dummyCoworker)
        .forEach(scenDelta => {
            const confirmedDummyCoworker = scenDelta.dummyCoworker!;
            const costCenters = getCoworkerCostCenters(Array.of(confirmedDummyCoworker), scenDelta);

            const coworkerGridRows: Array<CoworkerGridRow> = costCenters.map(ccId => {
                const editableCells = generateContractHoursEditableCellsProps({
                    coworker: undefined, // Always undefined for dummy coworkers, contract hours are derived from scenario deltas
                    delta: scenDelta,
                    timeArray: timeSelection.timeArray,
                    costCentre: ccId,
                    defaultContributions,
                    customContributions,
                });
                const rowSum = calculateSum(editableCells.map(p => (typeof p.currentValue === 'number' ? p.currentValue : 0)));

                return {
                    costCentre: ccId,
                    editableCells,
                    rowSum: costCentreList.includes(ccId) ? roundValue(rowSum) : 0,
                    rowAverage: roundValue(rowSum / editableCells.length) || 0,
                    rowType: CoworkerGridRowType.CONTRACT_HOURS,
                    headerContributions: editableCells.map((editableCell, index) => getHeaderContribution(
                        confirmedDummyCoworker,
                        editableCell,
                        ccId,
                        timeSelection.timeArray[index],
                        currentUnitRates,
                    )),
                };
            });

            scenarioCwsData.push({
                coworker: confirmedDummyCoworker,
                gridRows: coworkerGridRows,
            });
        });
    // coworker IS NOT dummy, actual PA coworker data for business unit
    Object.entries(coworkers ?? {})?.forEach(([personId, coworker]) => {
        if (!coworker || coworker.length === 0) {
            return;
        }
        const differencesInContracts: Array<Partial<Coworker>> = [];
        if (coworker.length > 1) {
            const comparableKeys: Array<keyof Coworker> = [
                'divisionName',
                'departmentName',
                'costCentre',
                'contractStartDate',
                'contractEndDate',
                'hoursPerWeek',
                'contractRange'
            ];
            coworker.sort((a, b) => new Date(a.validity[0]).getTime() - new Date(b.validity[0]).getTime());
            const coworkerKeys = Object.keys(coworker[0]) as Array<keyof Coworker>;
            // contractDifferences containing the changes in contract attributes and values
            for (let i = 1; i < coworker.length; i++) {
                const contractDifferences: Partial<Record<string, Coworker[keyof Coworker]>> = {};
                let hasComparableChange = false;
                coworkerKeys.forEach(key => {
                    if (comparableKeys.includes(key)) {
                        if (!isEqual(coworker[i - 1][key], coworker[i][key])) {
                            hasComparableChange = true;
                            contractDifferences[key] = coworker[i][key];
                        }
                    }
                });
                if (hasComparableChange) {
                    // Keep the end date on every future data item, even when unchanged.
                    contractDifferences.contractEndDate = coworker[i].contractEndDate;
                    differencesInContracts.push(contractDifferences);
                }
            }
        }
        const cwScenarioDelta = getContractDelta(personId, scenario) ?? null;

        const costCentres = Array.from(new Set(getCoworkerCostCenters(coworker, cwScenarioDelta)));
        const gridRows: Array<CoworkerGridRow> = [];
        costCentres.forEach(ccId => {
            if (!costCentreIsInList(ccId, costCentreList)) {
                return; // Skip this coworker if they don't belong to any cost centre in the list
            }
            const editableCells = generateContractHoursEditableCellsProps({
                coworker,
                delta: cwScenarioDelta,
                timeArray: timeSelection.timeArray,
                costCentre: ccId,
                defaultContributions,
                customContributions,
                modulationData,
            });
            const rowSum = calculateSum(editableCells.map(p => (typeof p.currentValue === 'number' ? p.currentValue : 0)));
            const rowAverage = roundValue(rowSum / editableCells.length) || 0;

            const headerContributions = editableCells.map((editableCell, index) => {
                const week = editableCell.key.split(':')[2].replace(/-cell$/, '');
                const validContract = coworker.find(({ contractStartDate, contractEndDate }) => moment(week)
                    .isBetween(contractStartDate ?? BEGINNING_OF_TIME, contractEndDate ?? INDEFINITE_END_DATE, 'isoWeek', '[]')) ?? coworker[0];

                return getHeaderContribution(
                    validContract,
                    editableCell,
                    ccId,
                    timeSelection.timeArray[index],
                    currentUnitRates,
                );
            });

            gridRows.push({
                costCentre: ccId,
                editableCells,
                rowSum: costCentreIsInList(ccId, costCentreList) ? roundValue(rowSum) : 0,
                rowAverage,
                rowType: CoworkerGridRowType.CONTRACT_HOURS,
                headerContributions,
            });
        });

        paDataCoworkers.push({
            coworker: {
                ...coworker[0],
                ...(differencesInContracts.length > 0 ? { futureData: differencesInContracts } : {}),
            },
            gridRows,
        });
    });
    const gridData: Array<CoworkerGridData | DummyGridData> = Array.from([...scenarioCwsData, ...paDataCoworkers]);

    return gridData;
};

/**
 * Filters the gridRows by removing any coworkers that do not belong to any cost centre
 * in the costCentreList.
 * @param gridRowsData gridRows generated by generateGridRows
 * @param costCentreList List of cost centres
 * @returns \{coworkers: Array<(DummyGridData | CoworkerGridData)>}
 */
const filterGridDataByCostCentre = (gridRowsData: GridDataCoworkers | undefined, costCentreList: string[] | undefined) => {
    if (!gridRowsData) {
        return undefined;
    }

    return {
        coworkers: gridRowsData.coworkers
            .filter(coworker => !costCentreList || costCentreList.length === 0 || coworker.gridRows
                .some(row => costCentreIsInList(row.costCentre, costCentreList))) ?? [],
    };
};

/**
 * A function which merges two gridRows objects. The scenarioData object always takes priority over
 * the coworkerData object. So if a coworker is present in both objects, the coworker in scenarioData
 * overwrites the coworker in coworkerData when producing the final output.
 * @param coworkerData gridRows generated by generateGridRows without the scenario data. Contains all the coworkers in the unit
 * @param scenarioData gridRows generated by generateGridRows with only coworkers/dummys affected by the scenario data
 * @returns A merged object where coworkers in scenarioData replace corresponding coworkers in coworkerData
 */
const mergeRowsData = (
    coworkerData: GridDataCoworkers | undefined,
    scenarioData: GridDataCoworkers | undefined
): GridDataCoworkers | undefined => {
    if (!coworkerData && !scenarioData) {
        return undefined;
    }
    if (!coworkerData?.coworkers) {
        return cloneDeep(scenarioData);
    }
    if (!scenarioData?.coworkers) {
        return { coworkers: cloneDeep(coworkerData.coworkers) };
    }

    const mergedList = cloneDeep(coworkerData.coworkers);
    const clonedScenarioData = cloneDeep(scenarioData);
    clonedScenarioData.coworkers.forEach(scenarioCoworker => {
        if (scenarioCoworker.coworker.isDummy) {
            mergedList.unshift(scenarioCoworker);
        } else {
            mergedList[mergedList.findIndex(coworker => coworker.coworker.personId === scenarioCoworker.coworker.personId)] = scenarioCoworker;
        }
    });

    return {
        coworkers: mergedList
    };
};

/**
 * A helper function which generates gridrows for all coworkers/dummy coworkers which are in the scenario.
 * and have a cost centre in the costCentreList.
 * @param param0
 * @returns gridRows for all coworkers/dummy coworkers which are in the scenario.
 */
const getScenarioGridRows = (
    {
        costCentreList,
        orgData,
        currentUnit,
        currentUnitType,
        coworkers,
        currentScenario,
        timeSelection,
        currentUnitRates,
        defaultContributions,
        customContributions,
        modulationData,
    } : {
        costCentreList: Array<string> | undefined,
        orgData: OrgData | undefined,
        currentUnit: string,
        currentUnitType: string,
        coworkers: Coworkers | undefined,
        currentScenario: Scenario | undefined,
        timeSelection: TimeSelection,
        currentUnitRates: InputRates | undefined,
        defaultContributions: DefaultContributions | undefined,
        customContributions: CustomContributions | undefined,
        modulationData: Modulation | undefined,
    }
) => {
    const costCentres = costCentreList ?? getAllCostCentresInUnit(orgData, currentUnit, currentUnitType);
    const coworkerIdsInScenario = currentScenario?.scenarioData
        .filter(data => data.coworkerSource === 'PA')
        .map(data => data.personId) ?? [];
    const coworkersInScenario = Object.fromEntries(Object.entries(coworkers ?? {}).filter(([id]) => coworkerIdsInScenario.includes(id))) ?? [];

    return {
        coworkers: generateGridRows(
            {
                coworkers: coworkersInScenario,
                scenario: currentScenario,
                timeSelection,
                costCentreList: costCentres,
                currentUnitRates,
                defaultContributions,
                customContributions,
                modulationData,
            }
        )
    };
};

/**
 * A helper function which primarily keeps track of whether or not the
 * gridDataRef needs to be updated. If it does, it uses generateGridRows
 * on all the coworkers in all the cost centres and stores the data for future use.
 * @param param0
 * @returns CoworkerGridDataRef (which includes gridrows and trackers
 * for whether or not the data needs to be updated)
 */
const updateCoworkerGridDataRef = (
    {
        coworkerGrid,
        coworkers,
        timeSelection,
        user,
        currentUnit,
        currentUnitType,
        orgData,
        currentUnitRates,
        defaultContributions,
        customContributions,
        modulationData,
    }:
    {
        coworkerGrid: CoworkerGridDataRef | undefined,
        coworkers: Coworkers | undefined
        timeSelection: TimeSelection,
        user: UserProfile | null,
        currentUnit: string | undefined,
        currentUnitType: string | undefined,
        orgData: OrgData | undefined,
        currentUnitRates: InputRates | undefined,
        defaultContributions: DefaultContributions | undefined,
        customContributions: CustomContributions | undefined,
        modulationData: Modulation | undefined,
    }
) => {
    if (
        coworkerGrid
        && coworkerGrid.coworkers.length > 0
        && coworkerGrid.coworkers.length === Object.entries(coworkers ?? {}).length
        && isIdenticalTimeSelection(coworkerGrid.timeSelection, timeSelection)
        && currentUnit === coworkerGrid.currentUnit
        && orgData?.country === coworkerGrid.currentCountry
        && (coworkerGrid?.defaultContributionsIdentifier
            && defaultContributions && JSON.stringify(defaultContributions) === coworkerGrid.defaultContributionsIdentifier)
        && (coworkerGrid?.customContributionsIdentifier
            && customContributions && JSON.stringify(customContributions) === coworkerGrid.customContributionsIdentifier)
        && (coworkerGrid?.rateIdentifier
            && currentUnitRates && JSON.stringify(currentUnitRates) === coworkerGrid.rateIdentifier)
    ) {
        /* This prevents recalculations of the coworkerGridDataRef if nothing has changed.
        If we get here, it means that the coworkerGridDataRef is already up to date. */

        return coworkerGrid;
    }
    if (!user
        || !timeSelection
        || !timeSelection.valid
        || !currentUnit
        || !currentUnitType
        || !coworkers) {
        /* These are the required values to calculate the gridData.
            If any of them are missing, we return undefined */

        return undefined;
    }

    return {
        /*
            TODO: Figure out a way to get more efficient identifiers && other ways to reduce redundant calculations when moving timeselection
            Now it calculates once when timeselection updates, then again when rates api call is resolved, and this is the heaviest calculation.
            We want to suspend calculation pending the rates api call, and make sure we dont recalculate if nothing has changed.
            Add a recieved at timestamp in the front end and use as an identifier to replace stringify()?
            Note that different timeselections could in theory yield identical rates (if they span beyond end of last year), so making a validating function
            to prevent unneeded calculations can be difficult (i.e. if (!validateRatesUsingTimeSelection(timeSelection, currentUnitRates)) return undefined)).
        */
        rateIdentifier: currentUnitRates ? JSON.stringify(currentUnitRates) : 'no data',
        defaultContributionsIdentifier: defaultContributions ? JSON.stringify(defaultContributions) : 'no data',
        customContributionsIdentifier: customContributions ? JSON.stringify(customContributions) : 'no data',
        timeSelection,
        currentUnit,
        currentCountry: `${orgData?.country}`,
        coworkers: generateGridRows(
            {
                coworkers,
                scenario: undefined,
                timeSelection,
                costCentreList: getAllCostCentresInUnit(orgData, currentUnit, currentUnitType),
                currentUnitRates,
                defaultContributions,
                customContributions,
                modulationData,
            }
        ),
    };
};

export {
    mergeRowsData,
    getScenarioGridRows,
    updateCoworkerGridDataRef,
    generateContractHoursEditableCellsProps,
    generateGridRows,
    getContractDelta,
    filterGridDataByCostCentre,
    getCostCentreDescription
};
